import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { NmInput } from '../../components/NmInput';
import { getPlatformStore } from '../../platform';
import { comparisons as comparisonsApi } from '../../services/api';
import type { ComparisonStreamState } from './hooks/useComparisonStreams';
import type { ComparisonSession, SavedComparisonRecord } from './types';

interface SaveComparisonModalProps {
  open: boolean;
  session: ComparisonSession;
  streams: ComparisonStreamState;
  userId: number;
  onClose: () => void;
  onSaved: () => void;
}

function sparklineFromStream(stream: ComparisonStreamState['streamA']): number[] {
  return stream
    .filter((_, i) => i % Math.max(1, Math.floor(stream.length / 40)) === 0)
    .map(p => p.power ?? 0)
    .slice(0, 40);
}

export function SaveComparisonModal({ open, session, streams, userId, onClose, onSaved }: SaveComparisonModalProps) {
  const { t } = useTranslation();
  const defaultName =
    session.workoutA && session.workoutB
      ? t('compare.defaultNameVs', { a: session.workoutA.title, b: session.workoutB.title })
      : session.workoutA
        ? t('compare.defaultNameSegments', { title: session.workoutA.title })
        : t('compare.defaultName');

  const [name, setName] = useState(defaultName);
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const record: SavedComparisonRecord = {
      id: crypto.randomUUID(),
      name: name.trim() || defaultName,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      notes: notes.trim(),
      savedAt: new Date().toISOString(),
      session: { ...session },
      sparklineA: sparklineFromStream(streams.streamA),
      sparklineB: sparklineFromStream(streams.streamB),
    };

    try {
      await getPlatformStore().saveComparison(record as unknown as Record<string, unknown>);

      try {
        const res = await comparisonsApi.create(userId, {
          name: record.name,
          tags: record.tags,
          notes: record.notes,
          workout_a_id: session.workoutA!.id,
          workout_b_id: session.mode === 'one-workout-segments' ? null : session.workoutB?.id ?? null,
          segment_a: session.segmentA
            ? { start_sec: session.segmentA.startSec, end_sec: session.segmentA.endSec }
            : null,
          segment_b: session.segmentB
            ? { start_sec: session.segmentB.startSec, end_sec: session.segmentB.endSec }
            : null,
          config: {
            mode: session.mode,
            metric: session.metric,
            alignment: session.alignment,
            viewMode: session.viewMode,
          },
          sparkline_a: record.sparklineA,
          sparkline_b: record.sparklineB,
        });
        const apiId = (res.data as { data?: { id?: number } })?.data?.id;
        if (apiId) {
          await getPlatformStore().saveComparison({ ...record, apiId } as unknown as Record<string, unknown>);
        }
      } catch {
        /* local save succeeded; cloud optional offline */
      }

      onSaved();
    } catch (e: unknown) {
      setError((e as Error)?.message ?? t('compare.saveModal.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div className="modal-surface" style={modal} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px' }}>{t('compare.saveModal.title')}</h3>
        <label style={labelStyle}>{t('compare.saveModal.name')}</label>
        <NmInput value={name} onChange={e => setName(e.target.value)} />
        <label style={labelStyle}>{t('compare.saveModal.tags')}</label>
        <NmInput value={tags} onChange={e => setTags(e.target.value)} placeholder={t('compare.saveModal.tagsPlaceholder')} />
        <label style={labelStyle}>{t('compare.saveModal.notes')}</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          style={{ width: '100%', borderRadius: 8, padding: 8, border: '1px solid var(--surface-sunken)' }}
        />
        {error && <p style={{ color: 'var(--color-danger)', fontSize: 12 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <NmButton variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </NmButton>
          <NmButton variant="primary" loading={saving} onClick={() => void handleSave()}>
            {t('common.save')}
          </NmButton>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(26,31,43,.45)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 9000,
};

const modal: React.CSSProperties = {
  width: 'min(440px, 92vw)',
  background: 'var(--surface-0)',
  borderRadius: 'var(--r-4)',
  padding: 20,
  boxShadow: 'var(--nm-raised-md)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--fg-3)',
  marginTop: 10,
  marginBottom: 4,
};
